import React from "react";
import { useNavigate } from 'react-router-dom';
import { Building2, Home, Building, Sparkles, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import residentialImage from "../../assets/image/i1.jpg";
import commercialImage from "../../assets/image/c1.jpg";
import industrialImage from "../../assets/image/r1.jpg";

const Services = () => {
  const navigate = useNavigate();

  const handleLearnMore = () => {
    navigate('/servicepage'); 
  };

  const services = [
    {
      title: "Residential Buildings",
      description:
        "Comprehensive cleaning services for homes, apartments, and residential complexes. We ensure your living space stays pristine and healthy.",
      icon: Home,
      image: residentialImage,
      features: ["Deep Cleaning", "Window Cleaning", "Carpet Cleaning"],
    },
    {
      title: "Commercial Buildings",
      description:
        "Professional cleaning solutions for offices, retail spaces, and commercial properties. Maintain a spotless work environment.",
      icon: Building2,
      image: commercialImage,
      features: [
        "Office Cleaning",
        "Regular Cleaning",
        "Common Area Maintenance",
        "Sanitization Services",
        "Floor Care",
      ],
    },
    {
      title: "Industrial Buildings",
      description:
        "Specialized cleaning services for factories, warehouses, and industrial facilities. Expert solutions for demanding environments.",
      icon: Building,
      image: industrialImage,
      features: [
        "Factory Cleaning",
        "Warehouse Maintenance",
        "Equipment Cleaning",
        "Deep Cleaning",
        // "Safety Compliance",
      ],
    },
  ];

  return (
    <div className="relative overflow-hidden">
      {/* Background Gradients */}
      <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-gradient-to-br from-primary/10 to-transparent" />
      <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-gradient-to-tl from-secondary/10 to-transparent" />
      <div className="absolute top-1/4 right-0 w-64 h-64 rounded-full bg-gradient-to-bl from-primary/5 to-secondary/5 blur-3xl" />
      <div className="absolute bottom-1/4 left-0 w-64 h-64 rounded-full bg-gradient-to-tr from-secondary/5 to-primary/5 blur-3xl" />

      <div className="relative bg-gradient-to-br from-green-50 via-white to-blue-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center relative"
          >
            {/* Decorative elements */}
            <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 w-40 h-1 bg-gradient-to-r from-transparent via-primary to-transparent" />

            <h2 className="text-4xl font-bold sm:text-5xl">
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                Our Cleaning Services
              </span>
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              Professional cleaning solutions tailored to your specific needs
            </p>
          </motion.div>

          <div className="mt-20 space-y-24">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  key={service.title}
                  className="relative group"
                >
                  {/* Card Background Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-50/50 to-white/50 rounded-3xl transform -rotate-1 scale-105" />

                  <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div
                        className={`p-8 ${
                          index % 2 === 0 ? "lg:order-1" : "lg:order-2"
                        }`}
                      >
                        <div className="flex items-center mb-6">
                          <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-gradient-to-r from-primary to-secondary transform rotate-3 group-hover:rotate-6 transition-transform duration-300">
                            <IconComponent className="h-7 w-7 text-white" />
                          </div>
                          <h3 className="ml-4 text-3xl font-bold text-gray-900">
                            {service.title}
                          </h3>
                        </div>
                        <p className="text-lg text-gray-600 mb-6">
                          {service.description}
                        </p>
                        <ul className="space-y-3 mb-8">
                          {service.features.map((feature, idx) => (
                            <li
                              key={idx}
                              className="flex items-center text-gray-700 group"
                            >
                              <div className="h-2 w-2 bg-primary rounded-full mr-3 group-hover:scale-150 transition-transform duration-300" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                        <button
                          onClick={handleLearnMore}
                          className="inline-flex items-center px-6 py-3 rounded-xl text-white bg-gradient-to-r from-primary to-secondary hover:from-primary-dark hover:to-secondary-dark transition-all duration-300 transform hover:scale-105 hover:rotate-1 shadow-md hover:shadow-xl"
                        >
                          Learn More
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </button>
                      </div>
                      <div
                        className={`relative ${
                          index % 2 === 0 ? "lg:order-2" : "lg:order-1"
                        }`}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-2xl transform rotate-3 scale-105" />
                        <img
                          src={service.image}
                          alt={service.title}
                          className="relative rounded-2xl shadow-xl w-full h-[400px] object-cover transform transition-all duration-300 group-hover:scale-105 group-hover:rotate-1"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;
